import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { PageHeader } from "@/components/patterns";
import { Loader2, Pencil, Trash2 } from "lucide-react";

type Currency = { id: string; name: string; code: string; symbol: string };
type Country = { id: string; name: string; code: string; currency_id?: string | null };

/** What the open dialog is editing: kind of record, and the record itself (null = a new one) */
type Editing = { kind: "currency"; item: Currency | null } | { kind: "country"; item: Country | null };

const th = "h-[34px] border-b border-border px-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const td = "border-b border-border/60 px-2.5 py-2";

/** Currencies and countries together, as in the design; the routes /settings/currencies and /settings/countries both open it */
export function CurrenciesCountriesPage() {
  const { success, error } = useToast();
  const [currencies, setCurrencies] = useState<Currency[] | null>(null);
  const [countries, setCountries] = useState<Country[] | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [form, setForm] = useState({ name: "", code: "", symbol: "", currency_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [cur, cou] = await Promise.all([api.get("/api/Currency/List"), api.get("/api/Country/List")]);
      setCurrencies(unwrapArray<Currency>(cur));
      setCountries(unwrapArray<Country>(cou));
    } catch (err: any) {
      error("Could not load", err.message || "Please try again");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const open = (e: Editing) => {
    setEditing(e);
    setForm({
      name: e.item?.name || "",
      code: e.item?.code || "",
      symbol: e.kind === "currency" ? (e.item as Currency | null)?.symbol || "" : "",
      currency_id: e.kind === "country" ? String((e.item as Country | null)?.currency_id || "") : "",
    });
  };

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!editing) return;
    const currency = editing.kind === "currency";
    if (!form.name.trim() || !form.code.trim() || (currency ? !form.symbol.trim() : !form.currency_id)) return error("Validation Error", currency ? "Name, code and symbol are required" : "Name, code and currency are required");
    const body = currency
      ? { name: form.name.trim(), code: form.code.trim().toUpperCase(), symbol: form.symbol.trim() }
      : { name: form.name.trim(), code: form.code.trim().toUpperCase(), currency_id: form.currency_id };
    const base = currency ? "/api/Currency" : "/api/Country";
    setBusy(true);
    try {
      if (editing.item) await api.put(`${base}/Update/${editing.item.id}`, body);
      else await api.post(`${base}/Insert`, body);
      success(currency ? "Currency saved" : "Country saved", form.name.trim());
      setEditing(null);
      await load();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (kind: "currency" | "country", item: Currency | Country) => {
    if (!window.confirm(`Delete ${item.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/${kind === "currency" ? "Currency" : "Country"}/Delete/${item.id}`);
      success("Deleted", item.name);
      await load();
    } catch (err: any) {
      error("Could not delete", err.message || "It may be in use");
    }
  };

  const currencyOf = (id?: string | null) => currencies?.find((c) => String(c.id) === String(id));
  const rowActions = (kind: "currency" | "country", item: Currency | Country) => (
    <span className="flex justify-end gap-1">
      <Button variant="ghost" size="icon-sm" aria-label={`Edit ${item.name}`} onClick={() => open({ kind, item } as Editing)}>
        <Pencil className="size-3.5" />
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label={`Delete ${item.name}`} onClick={() => remove(kind, item)}>
        <Trash2 className="size-3.5 text-danger" />
      </Button>
    </span>
  );

  return (
    <div className="space-y-4">
      <PageHeader title="Currencies & countries" />
      <p className="text-[13px] text-muted-foreground">What you can quote in, and where your customers are.</p>

      {!currencies || !countries ? (
        <TableSkeleton rows={5} columns={4} />
      ) : (
        <div className="flex flex-wrap items-start gap-3">
          <section className="min-w-0 flex-[1_1_480px] rounded-[10px] border border-border bg-card px-3.5 py-3">
            <h2 className="text-sm font-semibold text-foreground">Currencies</h2>
            <div className="mt-2 overflow-x-auto rounded-[10px] border border-border">
              <table className="w-full min-w-[420px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="bg-muted/40">
                    <th className={th}>Currency</th>
                    <th className={`${th} w-[70px]`}>Code</th>
                    <th className={`${th} w-[70px]`}>Symbol</th>
                    <th className={`${th} w-[80px]`}>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currencies.map((c) => (
                    <tr key={c.id}>
                      <td className={`${td} font-medium`}>{c.name}</td>
                      <td className={`${td} font-mono`}>{c.code}</td>
                      <td className={td}>{c.symbol}</td>
                      <td className={td}>{rowActions("currency", c)}</td>
                    </tr>
                  ))}
                  {currencies.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">
                        No currencies yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={() => open({ kind: "currency", item: null })} className="mt-2 text-[13px] font-medium text-primary hover:underline">
              + Add currency
            </button>
          </section>

          <section className="min-w-0 flex-[1_1_380px] rounded-[10px] border border-border bg-card px-3.5 py-3">
            <h2 className="text-sm font-semibold text-foreground">Countries</h2>
            <p className="text-xs text-muted-foreground">Used for addresses and which currency a customer is quoted in by default.</p>
            <div className="mt-2 overflow-x-auto rounded-[10px] border border-border">
              <table className="w-full min-w-[340px] border-collapse text-[12.5px]">
                <thead>
                  <tr className="bg-muted/40">
                    <th className={th}>Country</th>
                    <th className={`${th} w-[60px]`}>Code</th>
                    <th className={`${th} w-[80px]`}>Currency</th>
                    <th className={`${th} w-[80px]`}>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {countries.map((c) => (
                    <tr key={c.id}>
                      <td className={`${td} font-medium`}>{c.name}</td>
                      <td className={`${td} font-mono`}>{c.code}</td>
                      <td className={`${td} font-mono`}>{currencyOf(c.currency_id)?.code || "—"}</td>
                      <td className={td}>{rowActions("country", c)}</td>
                    </tr>
                  ))}
                  {countries.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">
                        No countries yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={() => open({ kind: "country", item: null })} className="mt-2 text-[13px] font-medium text-primary hover:underline">
              + Add country
            </button>
          </section>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing?.item ? "Edit" : "Add"} {editing?.kind === "currency" ? "currency" : "country"}
            </DialogTitle>
            <DialogDescription>{editing?.kind === "currency" ? "Shown on quotes, for example INR and ₹." : "The country and the currency its customers are quoted in."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="font-mono uppercase" maxLength={editing?.kind === "currency" ? 3 : 2} />
              </div>
              {editing?.kind === "currency" ? (
                <div className="space-y-1.5">
                  <Label>Symbol</Label>
                  <Input value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <SearchableSelect value={form.currency_id} onChange={(e) => setForm({ ...form, currency_id: e.target.value })} className="h-9 w-full rounded-md border border-input bg-card px-2 text-sm">
                    <option value="">Choose…</option>
                    {(currencies || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.code} · {c.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
